'use client';

/**
 * Rounded, bordered course tile ported from the legacy LMS's
 * CourseCardHigh: cover with a corner ribbon badge, 2-line clamped
 * title, level pill under the title, progress bar, and a stats
 * footer (enrolled + duration) — the 'udemy' card's face/footer
 * treatment, without its hover popover.
 */
import { useTranslation } from 'react-i18next';
import { PortalLink } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { LevelBadge } from '../_shared/LevelBadge';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { cardReset } from '../_shared/styles';
import { CertificateBadge } from '../course-detail/shared/CertificateBadge';
import { isLiveCourse } from '../_shared/courseLive';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { CertificateDisplay, ProgressTextPlace } from './registry';

const BADGE_TONE: Record<string, string> = {
  brand: 'var(--color-brand-600, #3b5bdb)',
  success: 'var(--color-feedback-success, #15803d)',
  amber: 'var(--color-amber-600, #d97706)',
  muted: 'var(--color-neutral-600, #52525b)',
};

// Lift + shadow lives on the OUTER wrap rather than the inner
// `overflow: hidden` anchor (which clips the image's rounded corners) —
// a box-shadow painted on the same box that clips overflow gets clipped
// too in most engines, so the hover shadow would render partially or not
// at all if it sat on the inner element.
const CLASSIC_HIGH_CARD_CSS = `
.lms-classic-high-card-wrap {
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-xs, 0 1px 2px rgba(0,0,0,0.06));
  transition: transform 200ms ease, box-shadow 200ms ease;
}
.lms-classic-high-card-wrap:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(0,0,0,0.13), 0 2px 6px rgba(0,0,0,0.06);
}
@media (prefers-reduced-motion: reduce) {
  .lms-classic-high-card-wrap { transition: none; }
}
.lms-classic-high-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid var(--color-danger-500, #ef4444);
  color: var(--color-danger-500, #ef4444);
}
.lms-classic-high-live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: lms-classic-high-live-pulse 1.4s ease-in-out infinite;
}
@keyframes lms-classic-high-live-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
@media (prefers-reduced-motion: reduce) {
  .lms-classic-high-live-dot { animation: none; }
}
`;

/** {@code DD/MM/YYYY HH:mm:ss}, local time — same shape as the legacy
 *  card's dayjs format, no dayjs dependency needed for two fields. */
function formatDateTime(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function UsersIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ClockIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function ClassicHighCourseCard({
  course: c,
  certificateDisplay = 'both',
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  /** How the certificate marker renders on the cover, when the course has
   *  one: the badge image, the text label, or both. Mirrors the 'udemy'
   *  card's option. */
  certificateDisplay?: CertificateDisplay;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const { t } = useTranslation('common');
  const ribbonColor = c.badge
    ? (BADGE_TONE[c.badge.tone ?? 'brand'] ?? BADGE_TONE.brand)
    : undefined;
  const live = isLiveCourse(c);

  return (
    <div className="lms-classic-high-card-wrap" style={{ position: 'relative', height: '100%' }}>
      <style dangerouslySetInnerHTML={{ __html: CLASSIC_HIGH_CARD_CSS }} />
      {c.badge && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 0,
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <span
            style={{
              padding: '3px 12px 3px 10px',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.4,
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: 'var(--font-tracking-wide, 0.03em)',
              background: ribbonColor,
              borderTopRightRadius: 3,
              borderBottomRightRadius: 3,
              boxShadow: '0 2px 4px rgba(0,0,0,0.18)',
              whiteSpace: 'nowrap',
            }}
          >
            {c.badge.label}
          </span>
          <span
            aria-hidden
            style={{
              width: 0,
              height: 0,
              borderRight: '7px solid transparent',
              borderTop: `5px solid ${ribbonColor}`,
              filter: 'brightness(0.7)',
            }}
          />
        </div>
      )}

      <Card asChild>
        <PortalLink
          href={c.href}
          style={{
            ...cardReset,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: 'var(--color-surface-card, #fff)',
            border: '1px solid var(--color-border-default, #e0e0e0)',
            borderRadius: 'var(--radius-lg, 12px)',
            overflow: 'hidden',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          {/* Cover — bound to the shared aspect-ratio token so admins control
            every card variant's image shape from one setting. */}
          <div
            style={{
              flexShrink: 0,
              position: 'relative',
              overflow: 'hidden',
              aspectRatio: 'var(--app-course-image-aspect-ratio, 1.73)',
            }}
          >
            <CourseCoverImage
              image={typeof c.image === 'string' ? c.image : undefined}
              resizeTo="480x0"
            />
            {c.hasCertificate && (
              <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
                <CertificateBadge
                  label={t('label.certified')}
                  badgeUrl={c.certificateBadgeUrl}
                  mode={certificateDisplay}
                />
              </div>
            )}
          </div>

          {/* Body — title (2-line clamp) + level pill, matching CourseCardHigh's
            stacked title-then-level layout. */}
          <div
            style={{
              flex: 1,
              padding: '12px 14px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-md, 16px)',
                fontWeight: 600,
                lineHeight: 1.3,
                minHeight: 'calc(1.3em * 2)',
                color: 'var(--color-text-primary, #1c1d1f)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {c.title}
            </h3>

            {/* Live courses move the level pill into the live footer below,
              next to the start date, instead of duplicating it here. */}
            {!live && (
              <LevelBadge
                level={c.level}
                variant="solid"
                style={{
                  borderRadius: '8px',
                  padding: 'var(--space-1, 8px) var(--space-2, 16px)',
                  fontSize: 'var(--font-size-xs, 14px)',
                }}
              />
            )}
          </div>

          {!live && showProgress && typeof c.progress === 'number' && (
            <div style={{ flexShrink: 0, padding: '0 14px 12px' }}>
              <CourseProgressBar
                progress={c.progress}
                showText={showProgressText}
                textPlace={progressTextPlace}
              />
            </div>
          )}

          {/* Footer — enrolled + duration, same stats bar the 'udemy' card's
            face uses, minus its hover popover. Live courses swap this for
            the start/end session window instead (below). */}
          {!live && (
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '10px 14px',
                borderTop: '1px solid var(--color-border-default, #e0e0e0)',
                background: 'var(--color-surface-subtle, #f7f7f8)',
                fontSize: 13,
                color: 'var(--color-text-secondary, #4a4a4a)',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <UsersIcon />
                {typeof c.enrolled === 'number' && c.enrolled > 0
                  ? c.enrolled.toLocaleString()
                  : '-'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <ClockIcon />
                {c.duration ??
                  (typeof c.totalHours === 'number' ? `${c.totalHours} ${t('label.hours')}` : '-')}
              </span>
            </div>
          )}

          {/* Live footer — level pill + start date on the left, a pulsing
            LIVE badge + end date on the right. Replaces the stats bar
            above entirely; matches the legacy card's two-column layout. */}
          {live && (
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 14px',
                borderTop: '1px solid var(--color-border-default, #e0e0e0)',
                background: 'var(--color-surface-subtle, #f7f7f8)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <LevelBadge
                  level={c.level}
                  variant="solid"
                  style={{
                    borderRadius: '8px',
                    padding: '4px 8px',
                    fontSize: '10px',
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    fontSize: 11,
                    textAlign: 'center',
                    color: 'var(--color-text-secondary, #4a4a4a)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{t('field.startDate')}: </span>
                  <span>{formatDateTime(c.liveStartDate)}</span>
                </p>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span className="lms-classic-high-live-badge">
                  <span className="lms-classic-high-live-dot" />
                  {t('label.live')}
                </span>
                <p
                  style={{
                    margin: 0,
                    fontSize: 11,
                    textAlign: 'center',
                    color: 'var(--color-text-secondary, #4a4a4a)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{t('field.endDate')}: </span>
                  <span>{formatDateTime(c.liveEndDate)}</span>
                </p>
              </div>
            </div>
          )}
        </PortalLink>
      </Card>
    </div>
  );
}

ClassicHighCourseCard.gridSpec = {
  kind: 'grid',
  minCardWidth: 180,
  gap: 'clamp(12px, 2vw, 16px)',
} satisfies CardGridSpec;
